import React, { useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Message } from '@blink/components/src/constants/messages';

import { SupplyAmountField } from './SupplyAmountField';

import { Button, Text, TipCard, TooltipWrapper } from '@/shared/ui';
import { showModalAction } from '@/shared/store/modals/actions';
import { Store } from '@/shared/store';
import { formatWithCommas, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { useModal, useSupply } from '@/features/Modals';
import SupplyModalSchema, { SupplyModalData } from '@/shared/validations/supplyModal.schema';
import { ModalName } from '@/shared/store/modals/constants';
import { SummaryRow } from '@/widgets/Modals';
import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { useMaxBalance } from '@/features/Modals/ui/Pools/Supply/hooks/useMaxBalance';

const SupplyModalTexts = {
    title: 'How much do you want to supply?',
    tip: 'Arkis Liquidity (LP) tokens automatically earn interest proportional to your share of the pool.',
    tooltips: {
        poolUtilization: 'Total token supply being used by borrowers for their operations',
        poolBalance: 'Pool Balance shows the available tokens that have not been used by borrowers',
    },
};

enum SupplyModalFields {
    AMOUNT = 'amount',
}

export const PoolsSupplyModal = () => {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const dispatch = useDispatch();

    const { modalProps } = useModal();

    const {
        tokenSymbol,
        currentApy,
        totalDepositThreshold,
        decimals,
        poolUtilization,
        totalDeposited,
        refresh,
    } = modalProps || {};

    const maxBalance = useMaxBalance();

    const methods = useForm<SupplyModalData>({
        resolver: yupResolver(SupplyModalSchema),
        reValidateMode: 'onChange',
        context: { maxBalance },
    });

    const { makeSupply } = useSupply();

    const currentModal = {
        activeModal: ModalName.POOLS_SUPPLY_MODAL,
        modalTitle: 'Supply Liquidity',
        modalProps,
    };

    const onSubmit = async (data: SupplyModalData) => {
        if (!walletAddress) {
            dispatch(
                showModalAction({
                    modalName: ModalName.CONNECT_WALLET_MODAL,
                    modalTitle: 'Connect Wallet',
                    previousModal: currentModal,
                }),
            );
            return;
        }

        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: OperationStatusType.Pending,
                    description: Message.PENDING_TEXT,
                },
                previousModal: currentModal,
            }),
        );

        await makeSupply({
            amount: data[SupplyModalFields.AMOUNT],
            agreementAddress: modalProps?.agreementAddress,
            tokenAddress: modalProps?.tokenAddress,
        });

        if (refresh) {
            await refresh();
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className={'w-[448px] pt-7'}>
                <Text className='mb-3 font-medium' name='supply-modal-title'>
                    {SupplyModalTexts.title}
                </Text>
                <SupplyAmountField name={SupplyModalFields.AMOUNT} />

                <div className='mt-7'>
                    <TipCard text={SupplyModalTexts.tip} title='Tip' />
                </div>

                <div className=' border-b pb-7 mb-7 mt-5 border-border-opaque'>
                    <SummaryRow label='Current APY' value={`${currentApy}%`} />

                    <SummaryRow label='Pool Utilization' value={`${poolUtilization}%`}>
                        <TooltipWrapper text={SupplyModalTexts.tooltips.poolUtilization} />
                    </SummaryRow>

                    <SummaryRow label='Pool Balance' className={'border-none'}>
                        <div className='flex gap-2'>
                            <Text
                                name='pool-total-deposited'
                                type={Text.types.Paragraph}
                                fontWeight={Text.weights.SemiBold}
                            >{`${formatWithCommas(
                                formatWithMaxPrecision(totalDeposited, decimals),
                            )} ${tokenSymbol}`}</Text>
                            <Text
                                name='pool-total-deposited-threshold'
                                type={Text.types.Paragraph}
                                fontWeight={Text.weights.Regular}
                                className='text-content-tertiary'
                            >{`/ ${formatWithCommas(totalDepositThreshold)} ${tokenSymbol}`}</Text>
                        </div>
                        <TooltipWrapper text={SupplyModalTexts.tooltips.poolBalance} />
                    </SummaryRow>
                </div>

                <SubmitButton />
            </form>
        </FormProvider>
    );
};

function SubmitButton() {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const { watch, getFieldState } = useFormContext();
    const { modalProps } = useModal();
    const { tokenSymbol } = modalProps || {};
    const amount = watch(SupplyModalFields.AMOUNT);
    const { error } = getFieldState(SupplyModalFields.AMOUNT);

    const maxBalance = useMaxBalance();

    const getSubmitButtonText = () => {
        return !walletAddress ? 'Connect wallet' : `Approve ${tokenSymbol}`;
    };

    const isDisabled = useMemo(() => {
        if (error || !amount || Number(amount) === 0) return true;
        return Number(maxBalance) < Number(amount);
    }, [amount, maxBalance, walletAddress, error]);

    return (
        <Button
            size={Button.sizes.Large}
            disabled={isDisabled}
            className='w-full font-semibold'
            type='submit'
        >
            {getSubmitButtonText()}
        </Button>
    );
}
